import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls = "mt-1 w-full rounded-lg border border-white/10 bg-charcoal-800 px-3 py-2.5 text-sm text-cream focus:border-copper focus:outline-none [color-scheme:dark]";
const labelCls = "block text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400";
const addBtnCls = "inline-flex items-center gap-1.5 rounded-full border border-copper/40 px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] text-copper-300 hover:bg-copper/10";

const STATUS_STYLES = {
  pending: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  confirmed: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  cancelled: "border-red-400/40 bg-red-400/10 text-red-300",
};

function fmtINR(n) {
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

export function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => { api.get("/admin/bookings").then((r) => setBookings(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const setStatus = async (b, status) => {
    try {
      await api.put(`/admin/bookings/${b.id}`, { status });
      setBookings(bookings.map((x) => (x.id === b.id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setNote = async (b, payment_note) => {
    try {
      await api.put(`/admin/bookings/${b.id}`, { payment_note });
      toast.success("Note saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!bookings.length) return <p className="text-sm text-cream-muted">No booking requests yet. They'll appear here the moment a guest reserves dates.</p>;

  return (
    <div className="space-y-3" data-testid="admin-bookings-list">
      {bookings.map((b) => (
        <div key={b.id} className="rounded-xl border border-white/10 bg-charcoal-800/60 p-4" data-testid={`booking-row-${b.id.slice(0, 8)}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-cream">
                <span className="font-mono text-copper-300">MCV-{b.id.slice(0, 8).toUpperCase()}</span> · {b.name} · {b.phone}
              </p>
              <p className="mt-1 text-xs text-cream-muted">
                {b.checkin} → {b.checkout} · {b.nights} night{b.nights === 1 ? "" : "s"} · {b.guests} guests · {b.unit}
                {b.amount_total ? ` · Total ${fmtINR(b.amount_total)} · Advance ${fmtINR(b.amount_advance)}` : ""}
              </p>
              {b.notes && <p className="mt-1 text-xs italic text-cream-dim">"{b.notes}"</p>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${STATUS_STYLES[b.status]}`}>{b.status}</span>
              {b.status !== "confirmed" && (
                <button onClick={() => setStatus(b, "confirmed")} data-testid={`booking-confirm-btn-${b.id.slice(0, 8)}`}
                  className="rounded-full border border-emerald-400/40 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-400/10">Confirm</button>
              )}
              {b.status !== "cancelled" && (
                <button onClick={() => setStatus(b, "cancelled")} data-testid={`booking-cancel-btn-${b.id.slice(0, 8)}`}
                  className="rounded-full border border-red-400/40 px-3 py-1.5 text-xs text-red-300 hover:bg-red-400/10">Cancel</button>
              )}
              <a href={`https://wa.me/${b.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" aria-label="WhatsApp guest"
                className="rounded-full border border-copper/40 p-2 text-copper-300 hover:bg-copper/10"><MessageCircle className="h-3.5 w-3.5" /></a>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <input defaultValue={b.payment_note || ""} placeholder="Payment note (e.g. UPI ref, received 50%)…" className={inputCls}
              data-testid={`booking-note-input-${b.id.slice(0, 8)}`}
              onKeyDown={(e) => e.key === "Enter" && setNote(b, e.target.value)} />
            <button onClick={(e) => setNote(b, e.target.previousSibling.value)}
              className="shrink-0 rounded-full border border-white/15 px-4 text-xs text-cream-muted hover:border-copper/40 hover:text-copper-300">Save note</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function CalendarTab() {
  const [ranges, setRanges] = useState([]);
  const [form, setForm] = useState({ from_date: "", to_date: "", note: "" });
  const load = () => api.get("/admin/blocked").then((r) => setRanges(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blocked", form);
      setForm({ from_date: "", to_date: "", note: "" });
      load();
      toast.success("Dates blocked");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => {
    await api.delete(`/admin/blocked/${id}`);
    setRanges(ranges.filter((r) => r.id !== id));
  };

  return (
    <div data-testid="admin-calendar-tab">
      <p className="text-sm text-cream-muted">Block dates that are booked on other channels (OTA, phone bookings). Blocked dates can't be requested on the site — this prevents double-bookings.</p>
      <form onSubmit={add} className="mt-6 grid items-end gap-3 rounded-xl border border-white/10 bg-charcoal-800/60 p-4 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <label className="block"><span className={labelCls}>From</span>
          <input type="date" required value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="blocked-from-input" className={inputCls} /></label>
        <label className="block"><span className={labelCls}>To</span>
          <input type="date" required value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="blocked-to-input" className={inputCls} /></label>
        <label className="block"><span className={labelCls}>Note</span>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Booked on MakeMyTrip" data-testid="blocked-note-input" className={inputCls} /></label>
        <button type="submit" className={addBtnCls} data-testid="blocked-add-btn"><Plus className="h-3.5 w-3.5" /> Block</button>
      </form>
      <div className="mt-6 space-y-2">
        {ranges.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-charcoal-800/60 px-4 py-3" data-testid={`blocked-item-${r.id.slice(0, 8)}`}>
            <p className="text-sm text-cream">{r.from} → {r.to} {r.note && <span className="text-cream-dim">· {r.note}</span>}</p>
            <button onClick={() => remove(r.id)} aria-label="Remove blocked range" className="text-cream-dim hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!ranges.length && <p className="text-sm text-cream-dim">No manually blocked dates.</p>}
      </div>
    </div>
  );
}
